import React, { useEffect, useState } from 'react';
import { BellRing, CalendarOff, Trash2 } from 'lucide-react';
import { api } from '../lib/api.js';
import {
  HOURS,
  OVERRIDE_KINDS,
  REASON_LABEL,
  WEEKDAYS,
  formatDate,
  formatDay,
  gapLabel,
  overrideLabel,
  overridesByCountry,
  toForm,
  toSettings,
  weekdayLabel,
} from '../lib/timesheetReminders.js';
import {
  countryOptions,
  loadHolidayCountries,
} from '../lib/holidayCountries.js';
import { Card } from '../components/ui.jsx';
import { PageHeader } from '../components/PageSkeleton.jsx';
import { Select } from '../components/controls/Select.jsx';
import { DatePicker } from '../components/controls/DatePicker.jsx';
import { UserMultiSelect } from '../components/UserMultiSelect.jsx';

// Reminders (admins only, Management in the sidebar): one global schedule
// for the whole company, a preview of what the next escalation run would
// send and to whom, and the exceptions to the computed public-holiday
// calendars. Everything comes from GET /admin/timesheet-reminders; a save,
// or adding or removing an exception, re-fetches so the preview follows.
export function TimesheetReminders({ users }) {
  const [state, setState] = useState(null);
  const [form, setForm] = useState(null);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState([]);
  const [override, setOverride] = useState({
    country: 'CZ',
    date: '',
    kind: 'off',
    name: '',
  });

  async function load() {
    const result = await api('/admin/timesheet-reminders');
    setState(result);
    setForm(toForm(result.settings));
  }

  // The page state without the form: an exception changes the preview, but
  // must not throw away unsaved edits to the settings.
  async function refresh() {
    setState(await api('/admin/timesheet-reminders'));
  }

  useEffect(() => {
    load().catch((err) => setMessage(err.message));
    loadHolidayCountries()
      .then(setCountries)
      .catch((err) => setMessage(err.message));
  }, []);

  async function save(event) {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      await api('/admin/timesheet-reminders', {
        method: 'PUT',
        body: JSON.stringify(toSettings(form)),
      });
      await load();
      setMessage('Saved.');
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function addOverride(event) {
    event.preventDefault();
    setMessage('');
    try {
      await api('/admin/holiday-overrides', {
        method: 'POST',
        body: JSON.stringify({
          country: override.country,
          date: override.date,
          observed: override.kind === 'off',
          name: override.name,
        }),
      });
      setOverride({ ...override, date: '', name: '' });
      await refresh();
    } catch (err) {
      setMessage(err.message);
    }
  }

  async function removeOverride(item) {
    setMessage('');
    try {
      await api(`/admin/holiday-overrides/${item.country}/${item.date}`, {
        method: 'DELETE',
      });
      await refresh();
    } catch (err) {
      setMessage(err.message);
    }
  }

  const status = !form
    ? 'Loading…'
    : !form.enabled
      ? 'Off'
      : form.reportOnly
        ? 'Report only'
        : 'On';

  return (
    <div className="reminders-page">
      <PageHeader
        title="Reminders"
        subtitle={`Timesheet reminders · ${status}${
          state && !state.mailConfigured
            ? ' · mail is not configured on this server'
            : ''
        }`}
      />
      {!state || !form ? (
        <Card title="Schedule">
          <p className="hint">{message || 'Loading…'}</p>
        </Card>
      ) : (
        <>
          <Card title="Schedule">
            <div className="outlook-settings-copy">
              <BellRing size={20} />
              <div>
                <strong>One rule for everyone</strong>
                <p>
                  Every {weekdayLabel(form.reminderWeekday)} at{' '}
                  {HOURS[Number(form.sendHour)]?.label}, people with an empty
                  working day in the last {form.lookbackWeeks}{' '}
                  {form.lookbackWeeks === '1' ? 'week' : 'weeks'} get one e-mail
                  listing the days. On {weekdayLabel(form.escalationWeekday)}{' '}
                  those whose last week is still incomplete get it again, and
                  their team manager gets one summary of the team. People
                  without a team manager, and a manager who is the one not
                  logging, go to the fallback recipients. A day logged on an
                  absence project counts as covered; public holidays and
                  weekends are never asked for.
                </p>
              </div>
            </div>
            <form className="form-grid reminders-form" onSubmit={save}>
              <label className="check">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(event) =>
                    setForm({ ...form, enabled: event.target.checked })
                  }
                />{' '}
                Send timesheet reminders
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  checked={form.reportOnly}
                  onChange={(event) =>
                    setForm({ ...form, reportOnly: event.target.checked })
                  }
                />{' '}
                Report only — compute everything, but mail just the fallback
                recipients a digest of what would be sent
              </label>
              <div className="two">
                <label>
                  Reminder day
                  <Select
                    ariaLabel="Reminder day"
                    value={form.reminderWeekday}
                    onChange={(reminderWeekday) =>
                      setForm({ ...form, reminderWeekday })
                    }
                    options={WEEKDAYS}
                  />
                </label>
                <label>
                  Escalation day
                  <Select
                    ariaLabel="Escalation day"
                    value={form.escalationWeekday}
                    onChange={(escalationWeekday) =>
                      setForm({ ...form, escalationWeekday })
                    }
                    options={WEEKDAYS}
                  />
                </label>
              </div>
              <div className="two">
                <label>
                  Send after
                  <Select
                    ariaLabel="Send after"
                    value={form.sendHour}
                    onChange={(sendHour) => setForm({ ...form, sendHour })}
                    options={HOURS}
                  />
                </label>
                <label>
                  Weeks back
                  <input
                    type="number"
                    min={1}
                    max={12}
                    value={form.lookbackWeeks}
                    onChange={(event) =>
                      setForm({ ...form, lookbackWeeks: event.target.value })
                    }
                    required
                  />
                </label>
              </div>
              <label>
                Fallback recipients
                <UserMultiSelect
                  users={users}
                  selected={form.fallbackRecipientIds}
                  onChange={(fallbackRecipientIds) =>
                    setForm({ ...form, fallbackRecipientIds })
                  }
                  placeholder="Every admin"
                />
                <span className="hint">
                  Get the escalations nobody else can, and the report-only
                  digest. Empty means every active admin.
                </span>
              </label>
              <div className="reminders-actions">
                <span className="hint">{message}</span>
                <button className="primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save reminders'}
                </button>
              </div>
            </form>
          </Card>

          <Card
            title={`If the escalation ran today (${formatDate(state.preview.today)})`}
          >
            <p className="hint">
              Checked period {formatDay(state.preview.window.from)} –{' '}
              {formatDay(state.preview.window.to)}; last week{' '}
              {formatDay(state.preview.window.lastWeek.from)} –{' '}
              {formatDay(state.preview.window.lastWeek.to)}. Fallback
              recipients:{' '}
              {state.preview.fallbackRecipients.map((p) => p.name).join(', ') ||
                'none'}
              .
            </p>
            <div className="reminders-columns">
              <PreviewList
                title={`Escalated (${state.preview.people.length})`}
                empty="Nobody has a gap in last week."
                items={state.preview.people.map((person) => ({
                  key: person.user.id,
                  label: gapLabel(person),
                  note: person.escalated ? 'escalated' : '',
                  days: person.missing,
                }))}
              />
              <PreviewList
                title={`Manager summaries (${state.preview.managers.length})`}
                empty="Nothing to escalate."
                items={state.preview.managers.map((group) => ({
                  key: group.manager.id,
                  label: group.manager.name,
                  note: group.people.map(gapLabel).join(', '),
                }))}
              />
              <PreviewList
                title={`Nobody to escalate to (${state.preview.unmanaged.length})`}
                empty="Everyone escalated has a team manager."
                items={state.preview.unmanaged.map((person) => ({
                  key: person.user.id,
                  label: gapLabel(person),
                  note: REASON_LABEL[person.reason] || person.reason,
                  days: person.missing,
                }))}
              />
            </div>
          </Card>

          <Card title="Public holidays">
            <div className="outlook-settings-copy">
              <CalendarOff size={20} />
              <div>
                <strong>Computed automatically</strong>
                <p>
                  The national public holidays of about 200 countries, for every
                  year. A holiday is never a required day for people of that
                  country (set per member in Team → Edit member); below are the
                  countries someone is on. Add an exception when a holiday is
                  cancelled or the company gives an extra day off.
                </p>
              </div>
            </div>
            <div className="reminders-columns">
              {overridesByCountry(state.holidayOverrides, countries, users).map(
                (country) => (
                  <div key={country.value} className="reminders-list">
                    <strong>{country.label} · exceptions</strong>
                    {country.overrides.length === 0 ? (
                      <p className="hint">
                        None — the computed calendar applies.
                      </p>
                    ) : (
                      <ul>
                        {country.overrides.map((item) => (
                          <li key={item.date}>
                            <span>{overrideLabel(item)}</span>
                            <button
                              type="button"
                              className="icon-button"
                              aria-label={`Remove exception ${item.date}`}
                              data-tooltip="Remove exception"
                              onClick={() => removeOverride(item)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ),
              )}
            </div>
            <form className="form-grid reminders-add" onSubmit={addOverride}>
              <div className="three">
                <label>
                  Country
                  <Select
                    ariaLabel="Exception country"
                    value={override.country}
                    onChange={(country) =>
                      setOverride({ ...override, country })
                    }
                    options={countryOptions(countries, override.country)}
                  />
                </label>
                <label>
                  Date
                  <DatePicker
                    ariaLabel="Exception date"
                    value={override.date}
                    onChange={(date) => setOverride({ ...override, date })}
                    required
                  />
                </label>
                <label>
                  Kind
                  <Select
                    ariaLabel="Exception kind"
                    value={override.kind}
                    onChange={(kind) => setOverride({ ...override, kind })}
                    options={OVERRIDE_KINDS}
                  />
                </label>
              </div>
              <label>
                Name
                <input
                  value={override.name}
                  onChange={(event) =>
                    setOverride({ ...override, name: event.target.value })
                  }
                  placeholder="Optional"
                />
              </label>
              <div className="reminders-actions">
                <span />
                <button className="secondary" disabled={!override.date}>
                  Add exception
                </button>
              </div>
            </form>
          </Card>
        </>
      )}
    </div>
  );
}

function PreviewList({ title, empty, items }) {
  return (
    <div className="reminders-list">
      <strong>{title}</strong>
      {items.length === 0 ? (
        <p className="hint">{empty}</p>
      ) : (
        <ul>
          {items.map((item) => (
            <li key={item.key}>
              <span>
                {item.label}
                {item.note ? <em> · {item.note}</em> : null}
                {item.days?.length ? (
                  <small>{item.days.map(formatDay).join(', ')}</small>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
